import { Button, Flex, Form } from '@/components'
import { FullScreenModal } from '@/components/derived/FullScreenModal/FullScreenModal.component'
import { useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { Edit } from '@icon-park/react'
import { KeyMessageResponse, UpdateKeyMessageRulesRequest } from '@shared/models'
import { ChangeEvent, memo, useEffect, useState } from 'react'
import { useCompanyContext } from '../../CompanyDetails.context'
import {
  KeyMessageCountSpan,
  KeyMessageFormSection,
  KeyMessageTextArea,
  KeyMessageTitle,
  KeyMessageTitleWithCounter,
} from '../KeyMessage.styles'

interface EditKeyMessageModalProps {
  keyMessage: KeyMessageResponse | null
  onSuccess: () => void
}

const EditKeyMessageModalComponent = ({ keyMessage, onSuccess }: EditKeyMessageModalProps) => {
  const { queryParams, changeQueryParam } = useRouting()
  const { company } = useCompanyContext()
  const { sendAlert } = Alert.Hook()

  const [premises, setPremises] = useState('')
  const [exclusionPremises, setExclusionPremises] = useState('')

  const { isRunning: isUpdating, run: updateKeyMessage } = Api.Command<void>(
    Api.Methods.PATCH,
    `key-messages/:companyID/:message/rules`,
  )

  const companyID = company?.id

  useEffect(() => {
    if (!keyMessage) return

    setPremises(keyMessage.premises)
    setExclusionPremises(keyMessage.exclusionPremisses || '')
  }, [keyMessage])

  function onSubmit(): void {
    if (!premises.trim()) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'Premissas são obrigatórias',
      })
    }

    if (!keyMessage) return

    const input: UpdateKeyMessageRulesRequest = {
      premises: premises.trim(),
      internalID: keyMessage.internalID,
      ...(exclusionPremises.trim() && { exclusionPremisses: exclusionPremises.trim() }),
    }

    updateKeyMessage<UpdateKeyMessageRulesRequest>({
      binds: [
        ['companyID', String(companyID)],
        ['message', encodeURIComponent(keyMessage.message)],
      ],
      input,
      onSuccess: () => {
        sendAlert({
          type: 'success',
          title: 'Sucesso',
          message: 'Premissas atualizadas com sucesso!',
        })
        onSuccess()
        changeQueryParam({ param: 'modal', value: 'mensagem-chave', replaceAll: true })
      },
      onFail: () => {
        sendAlert({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao atualizar premissas',
        })
      },
    })
  }

  function handlePremisesChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    setPremises(event.target.value)
  }

  function handleExclusionPremisesChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    setExclusionPremises(event.target.value)
  }

  const isOpen = queryParams.modal === 'edit-key-message' && !!keyMessage

  const premisesCount = premises.trim() ? premises.split(';').filter((p) => p.trim().length > 0).length : 0
  const exclusionPremisesCount =
    exclusionPremises.trim() ? exclusionPremises.split(';').filter((p) => p.trim().length > 0).length : 0

  if (!keyMessage || !isOpen) return null

  return (
    <FullScreenModal
      controlKey="edit-key-message"
      backToName={company?.name}
      title="Editar Premissas"
      subtitle="Edite as premissas da mensagem-chave selecionada"
      icon={Edit}
      beforeClose={() => {
        onSuccess()
        changeQueryParam({ param: 'modal', value: 'mensagem-chave', replaceAll: true })
      }}>
      <Form onSubmit={onSubmit} controlParam={isUpdating}>
        <Flex $type="column" $align="stretch" $gap={24} $fit>
          <KeyMessageFormSection>
            <KeyMessageTitleWithCounter>
              <KeyMessageTitle>Mensagem (não editável)</KeyMessageTitle>
            </KeyMessageTitleWithCounter>
            <div
              style={{
                padding: '12px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #dee2ef',
                borderRadius: '8px',
                color: '#6b7280',
                fontSize: '14px',
              }}>
              {keyMessage.message}
            </div>
          </KeyMessageFormSection>

          <KeyMessageFormSection>
            <KeyMessageTitleWithCounter>
              <KeyMessageTitle>Premissas *</KeyMessageTitle>
              <KeyMessageCountSpan>{premisesCount} premissa(s)</KeyMessageCountSpan>
            </KeyMessageTitleWithCounter>
            <KeyMessageTextArea
              placeholder="Digite as premissas separadas por ponto e vírgula (;)&#10;Exemplo: inovação; tecnologia; transformação digital"
              value={premises}
              onChange={handlePremisesChange}
            />
          </KeyMessageFormSection>

          <KeyMessageFormSection>
            <KeyMessageTitleWithCounter>
              <KeyMessageTitle>Premissas de Exclusão</KeyMessageTitle>
              <KeyMessageCountSpan>{exclusionPremisesCount} premissa(s)</KeyMessageCountSpan>
            </KeyMessageTitleWithCounter>
            <KeyMessageTextArea
              placeholder="Digite as premissas de exclusão separadas por ponto e vírgula (;)&#10;Exemplo: palavras ou termos que devem ser excluídos da análise"
              value={exclusionPremises}
              onChange={handleExclusionPremisesChange}
            />
          </KeyMessageFormSection>

          <KeyMessageFormSection>
            <KeyMessageTitle>Marca: {keyMessage.brandName}</KeyMessageTitle>
          </KeyMessageFormSection>

          <KeyMessageFormSection>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: 8 }}>
              <KeyMessageTitle>Aplicando para: {keyMessage.dataSource.name}</KeyMessageTitle>
            </div>
          </KeyMessageFormSection>

          <Flex $type="row" $justify="flex-end" $align="stretch" $gap={16} $fit>
            <Button.Root
              type="button"
              $variant="secondary"
              onClick={() => {
                onSuccess()
                changeQueryParam({ param: 'modal', value: 'mensagem-chave', replaceAll: true })
              }}
              disabled={isUpdating}>
              <Button.Text value="Cancelar" />
            </Button.Root>

            <Button.Root type="button" $variant="primary" onClick={onSubmit} $loading={isUpdating}>
              <Button.Text value="Salvar Premissas" />
            </Button.Root>
          </Flex>
        </Flex>
      </Form>
    </FullScreenModal>
  )
}

EditKeyMessageModalComponent.displayName = 'EditKeyMessageModal'

export const EditKeyMessageModal = memo(EditKeyMessageModalComponent)
